import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  shiftMe, shiftLogin as apiLogin, shiftLogout as apiLogout,
  setShiftToken, getShiftToken, fetchMySchedule, patchMySchedule,
  fetchTreeProgress, connectShiftSocket,
} from '../lib/shiftApi';

const ShiftContext = createContext(null);

export const ALL_SHIFT_OPTIONS = [
  { id: 'early', label: '早班' },
  { id: 'late', label: '晚班' },
  { id: 'night', label: '夜班' },
  { id: 'maintenance', label: '維護班' },
];
export const SHIFT_OPTIONS = ALL_SHIFT_OPTIONS;
export const SUP_MODES = ALL_SHIFT_OPTIONS;
export const OWNER_MODES = ALL_SHIFT_OPTIONS;

/** 依 API／預設回傳可選班別選項 */
export function shiftOptionsFromIds(ids) {
  const set = new Set(Array.isArray(ids) && ids.length ? ids : ['early', 'late', 'night']);
  return ALL_SHIFT_OPTIONS.filter((s) => set.has(s.id));
}

/** 無 API 時的本地回退（與後端 default 一致） */
export function shiftOptionsForDate(bizDate) {
  const m = String(bizDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  let isSun = false;
  if (m) {
    isSun = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay() === 0;
  } else {
    const now = new Date();
    if (now.getHours() < 4) now.setDate(now.getDate() - 1);
    isSun = now.getDay() === 0;
  }
  if (isSun) return ALL_SHIFT_OPTIONS.filter((s) => s.id !== 'night');
  return ALL_SHIFT_OPTIONS.filter((s) => s.id !== 'maintenance');
}

const PICK_KEY = 'shift_picked_bizdate';

export function getShiftPickedDate() {
  try { return localStorage.getItem(PICK_KEY) || ''; } catch { return ''; }
}
export function setShiftPickedDate(d) {
  try {
    if (d) localStorage.setItem(PICK_KEY, d);
    else localStorage.removeItem(PICK_KEY);
  } catch { /* ignore */ }
}

export function ShiftProvider({ children }) {
  const [status, setStatus] = useState('checking');
  const [user, setUser] = useState(null);
  const [supModes, setSupModes] = useState(['early']);
  const [activeShift, setActiveShift] = useState(null);
  const [bizDate, setBizDate] = useState('');
  const [availableShifts, setAvailableShifts] = useState(['early', 'late', 'night']);
  const [progress, setProgress] = useState({ pct: 0, done: 0, total: 0 });
  const [specialProgress, setSpecialProgress] = useState({ pct: 0, done: 0, total: 0 });
  const [extraProgress, setExtraProgress] = useState({ pct: 0, done: 0, total: 0 });
  const [personalTasks, setPersonalTasks] = useState({ hasPersonal: false, personalDone: 0, personalTotal: 0, allDone: false });
  const [treeTick, setTreeTick] = useState(0);
  const [socket, setSocket] = useState(null);
  const [needsShiftPick, setNeedsShiftPick] = useState(false);
  const activeShiftRef = useRef(activeShift);
  const bizDateRef = useRef(bizDate);
  const tickTimer = useRef(null);
  activeShiftRef.current = activeShift;
  bizDateRef.current = bizDate;

  const refresh = useCallback(async () => {
    if (!getShiftToken()) { setStatus('anon'); setUser(null); return; }
    try {
      const r = await shiftMe();
      setUser(r.user);
      setStatus('ok');
    } catch {
      setShiftToken('');
      setUser(null);
      setStatus('anon');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const applyProgressLite = useCallback((r, shiftType) => {
    const st = shiftType || activeShiftRef.current || 'early';
    const byShift = r?.byShift || r;
    const p = byShift?.[st];
    if (p) {
      setProgress({ pct: p.pct || 0, done: p.done || 0, total: p.total || 0 });
      if (p.specialProgress) setSpecialProgress(p.specialProgress);
      else if (p.specialPct != null) setSpecialProgress((s) => ({ ...s, pct: p.specialPct }));
      if (p.personalTasks) setPersonalTasks(p.personalTasks);
    }
    if (r?.extraProgress) setExtraProgress(r.extraProgress);
    else if (p?.extraProgress) setExtraProgress(p.extraProgress);
    if (r?.personalTasks) setPersonalTasks(r.personalTasks);
  }, []);

  const refreshProgress = useCallback(async (shiftType) => {
    const st = shiftType || activeShiftRef.current || 'early';
    const d = bizDateRef.current || undefined;
    try {
      const r = await fetchTreeProgress(d);
      if (r?.availableShifts?.length) setAvailableShifts(r.availableShifts);
      if (r) applyProgressLite(r, st);
      return r;
    } catch {
      return null;
    }
  }, [applyProgressLite]);

  const refreshScheduleAndProgress = useCallback(async () => {
    try {
      const sch = await fetchMySchedule();
      setBizDate(sch.bizDate || '');
      if (sch.availableShifts?.length) setAvailableShifts(sch.availableShifts);
      const st = sch.shiftType || 'early';
      setActiveShift(st);
      await refreshProgress(st);
    } catch { /* ignore */ }
  }, [refreshProgress]);

  const bumpTreeTick = useCallback(() => {
    if (tickTimer.current) clearTimeout(tickTimer.current);
    tickTimer.current = setTimeout(() => {
      setTreeTick((t) => t + 1);
    }, 280);
  }, []);

  useEffect(() => {
    if (status !== 'ok' || !user) return undefined;
    let alive = true;
    (async () => {
      try {
        const sch = await fetchMySchedule();
        if (!alive) return;
        const bd = sch.bizDate || '';
        setBizDate(bd);
        const avail = sch.availableShifts?.length
          ? sch.availableShifts
          : shiftOptionsForDate(bd).map((s) => s.id);
        setAvailableShifts(avail);
        const st = sch.shiftType || avail[0] || 'early';
        setActiveShift(st);

        const role = user.role;
        if (role === 'owner') {
          setNeedsShiftPick(false);
          setSupModes([st || avail[0] || 'early']);
        } else {
          const picked = getShiftPickedDate();
          const need = !sch.scheduled || picked !== bd;
          setNeedsShiftPick(need);
          if (role === 'supervisor') {
            setSupModes([st || avail[0] || 'early']);
          }
        }

        // 輕量進度（整樹留給 TaskTree）
        try {
          const prog = await fetchTreeProgress(bd);
          if (!alive) return;
          if (prog) applyProgressLite(prog, st);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    })();

    const s = connectShiftSocket();
    setSocket(s);

    const joinRoom = () => {
      const d = bizDateRef.current;
      if (d) s.emit('join', `tree:${d}`);
    };
    s.on('connect', joinRoom);

    const onTreeOrProgress = () => {
      refreshProgress();
      bumpTreeTick();
    };
    const onSchedule = () => { refreshScheduleAndProgress(); };

    s.on('tree:node', onTreeOrProgress);
    s.on('tree:updated', onTreeOrProgress);
    s.on('tree:notes', onTreeOrProgress);
    s.on('schedule:updated', onSchedule);
    s.on('anomaly_updated', onTreeOrProgress);
    s.on('anomaly_created', onTreeOrProgress);

    return () => {
      alive = false;
      if (tickTimer.current) clearTimeout(tickTimer.current);
      try {
        s.off('tree:node', onTreeOrProgress);
        s.off('tree:updated', onTreeOrProgress);
        s.off('tree:notes', onTreeOrProgress);
        s.off('schedule:updated', onSchedule);
        s.off('anomaly_updated', onTreeOrProgress);
        s.off('anomaly_created', onTreeOrProgress);
        s.disconnect();
      } catch { /* ignore */ }
      setSocket(null);
    };
  }, [status, user, refreshProgress, refreshScheduleAndProgress, applyProgressLite, bumpTreeTick]);

  useEffect(() => {
    if (!socket || !bizDate) return;
    socket.emit('join', `tree:${bizDate}`);
  }, [socket, bizDate]);

  const login = useCallback(async (code) => {
    const r = await apiLogin(code);
    if (r.ok && r.token) {
      setShiftToken(r.token);
      setUser(r.user);
      setSupModes(['early']);
      setStatus('ok');
      setNeedsShiftPick(r.user?.role !== 'owner');
      return r.user;
    }
    throw new Error(r.error || 'login_failed');
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    setShiftToken('');
    setShiftPickedDate('');
    setUser(null);
    setSupModes(['early']);
    setActiveShift(null);
    setNeedsShiftPick(false);
    setProgress({ pct: 0, done: 0, total: 0 });
    setSpecialProgress({ pct: 0, done: 0, total: 0 });
    setExtraProgress({ pct: 0, done: 0, total: 0 });
    setPersonalTasks({ hasPersonal: false, personalDone: 0, personalTotal: 0, allDone: false });
    setStatus('anon');
  }, []);

  const changeShift = useCallback(async (shiftType) => {
    setActiveShift(shiftType);
    try {
      await patchMySchedule({ shiftType, date: bizDateRef.current || undefined });
      if (bizDateRef.current) setShiftPickedDate(bizDateRef.current);
      setNeedsShiftPick(false);
      await refreshProgress(shiftType);
      bumpTreeTick();
    } catch (e) {
      throw e;
    }
  }, [refreshProgress, bumpTreeTick]);

  const confirmShiftPick = useCallback(async (shiftType) => {
    await changeShift(shiftType);
  }, [changeShift]);

  const toggleSupMode = useCallback((id) => {
    // 單選：點選即切換
    setSupModes([id]);
  }, []);

  const setSupMode = useCallback((id) => {
    if (id) setSupModes([id]);
  }, []);

  const value = {
    status, user,
    supModes, setSupModes, toggleSupMode, setSupMode,
    activeShift, setActiveShift, changeShift, confirmShiftPick,
    needsShiftPick, setNeedsShiftPick,
    availableShifts, setAvailableShifts,
    bizDate, setBizDate,
    progress, setProgress, specialProgress, setSpecialProgress,
    extraProgress, setExtraProgress,
    personalTasks, setPersonalTasks,
    refreshProgress, treeTick, bumpTreeTick,
    socket,
    login, logout, refresh,
    isStaff: user?.role === 'staff',
    isSupervisor: user?.role === 'supervisor',
    isOwner: user?.role === 'owner',
  };
  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error('useShift must be used within ShiftProvider');
  return ctx;
}
